import { DOCUMENT } from '@angular/common';
import { EnvironmentProviders, inject, Injectable, provideAppInitializer } from '@angular/core';
import { BfAuthService } from '@brafton/skynet-angular-security-service';

import { isAuthBridgeLocation } from './auth-bridge-route';

/** Name of the cookie that mirrors the signed-in ID token for the auth bridge. */
export const BRAFTON_SESSION_COOKIE = 'brafton_session';

/**
 * Mirrors the signed-in user's Auth0 ID token into a `.brafton.com` cookie so
 * the embedded `/auth-bridge` iframe can read it cross-site via the Storage
 * Access API. The org auth library keeps its tokens in localStorage, which
 * storage partitioning hides from a third-party iframe; SAA reliably restores
 * cookies, not localStorage — hence this cookie.
 *
 * The JWT is base64url/dot-only, so it is cookie-safe and stored verbatim. This
 * is a JS-readable cookie, i.e. the same XSS exposure as the localStorage tokens
 * the auth library already writes.
 */
@Injectable({ providedIn: 'root' })
export class BraftonSessionCookieService {
  private readonly auth = inject(BfAuthService);
  private readonly document = inject(DOCUMENT);

  /** Keeps the cookie in lockstep with the auth library's ID token. */
  start(): void {
    // `tokenAsync` is a BehaviorSubject of the raw id_token: '' when signed out,
    // and emitted both on login and on session restore at service construction.
    this.auth.tokenAsync.subscribe((rawToken) => {
      if (rawToken) {
        this.write(rawToken);
      } else {
        this.clear();
      }
    });
  }

  private write(rawToken: string): void {
    this.document.cookie =
      `${BRAFTON_SESSION_COOKIE}=${rawToken}; Path=/;${this.domainAttribute()} Secure; SameSite=None`;
  }

  private clear(): void {
    this.document.cookie =
      `${BRAFTON_SESSION_COOKIE}=; Path=/;${this.domainAttribute()} Max-Age=0; Secure; SameSite=None`;
  }

  /**
   * `Domain=.brafton.com` when deployed there (so every `*.brafton.com` host —
   * and the cross-site iframe — sees it), or host-only on localhost where a
   * `.brafton.com` domain would be rejected.
   */
  private domainAttribute(): string {
    const hostname = this.document.location?.hostname ?? '';
    return hostname.endsWith('brafton.com') ? ' Domain=.brafton.com;' : '';
  }
}

/**
 * Wires {@link BraftonSessionCookieService.start} to run at app startup —
 * everywhere EXCEPT the `/auth-bridge` route.
 *
 * The bridge is a read-only probe running in a hidden cross-site iframe. Its
 * Angular instance has its own storage (partitioned, or simply a fresh context),
 * so `tokenAsync` emits '' there and the sync would `clear()` the cookie — the
 * bridge would delete the signed-in user's session cookie and then report
 * `auth: 'failed'` for reading the cookie it just removed. The bridge only ever
 * READS this cookie; the main app owns writing it.
 */
export function provideBraftonSessionCookieSync(): EnvironmentProviders {
  return provideAppInitializer(() => {
    if (isAuthBridgeLocation(inject(DOCUMENT).location)) {
      return;
    }
    inject(BraftonSessionCookieService).start();
  });
}
